import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { usePreferencesStore } from '../../stores';

export interface TypewriterProps {
  /** The full string to reveal. Changes mid-flight reset the animation. */
  text: string;
  /**
   * How fast to type, in characters per second. The default of `40` reads
   * close to a fast human typist. Higher = faster reveal. Set to `0` to
   * skip the animation and show everything immediately.
   */
  speed?: number;
  /** Milliseconds to wait before starting the reveal. */
  startDelay?: number;
  /** Render the visible slice however the caller wants — useful when the
   *  source is markdown, JSX with embedded styling, etc. The default just
   *  prints the slice as-is. */
  renderText?: (visible: string) => ReactNode;
  /** Show a blinking caret while typing. */
  showCursor?: boolean;
  /** Custom caret character / element. Defaults to a thin vertical bar. */
  cursor?: ReactNode;
  /** Fired once the full text has been revealed. */
  onComplete?: () => void;
  className?: string;
  style?: CSSProperties;
  /**
   * When true, the component re-animates whenever `text` changes (default).
   * When false, only the first non-empty `text` is animated; subsequent
   * changes are shown immediately. Useful when streaming progressive
   * updates that you don't want re-typing.
   */
  retypeOnTextChange?: boolean;
}

/**
 * Typewriter — character-by-character reveal of a text payload.
 *
 * Honours the user's reduced-motion preference: when set, the full text is
 * shown immediately with no animation. Pairs well with markdown chat
 * responses by passing a `renderText` that runs the visible slice through
 * the markdown renderer.
 *
 * Speed is expressed in *characters per second* so the perceived pace is
 * consistent regardless of message length. Internally we convert to a
 * per-tick interval; very small values (e.g. 1 cps) will of course look
 * choppy.
 */
export function Typewriter({
  text,
  speed = 40,
  startDelay = 0,
  renderText,
  showCursor = false,
  cursor = <span aria-hidden="true" style={{ marginLeft: 1 }}>▋</span>,
  onComplete,
  className,
  style,
  retypeOnTextChange = true,
}: TypewriterProps) {
  const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
  const [visibleLength, setVisibleLength] = useState(reducedMotion ? text.length : 0);
  const onCompleteRef = useRef(onComplete);
  const lastTextRef = useRef<string>('');

  // Keep the latest onComplete callback without re-running the typing
  // effect just because the parent re-rendered with a new closure.
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    // Reduced-motion users bypass the animation entirely.
    if (reducedMotion || speed <= 0) {
      setVisibleLength(text.length);
      onCompleteRef.current?.();
      lastTextRef.current = text;
      return;
    }

    // When retypeOnTextChange is false, only the first non-empty text
    // animates; later changes snap straight to full length.
    if (!retypeOnTextChange && lastTextRef.current.length > 0 && lastTextRef.current !== text) {
      setVisibleLength(text.length);
      lastTextRef.current = text;
      return;
    }

    if (!text) {
      setVisibleLength(0);
      lastTextRef.current = '';
      return;
    }

    // Reset to 0 whenever the source text actually changes.
    if (lastTextRef.current !== text) {
      setVisibleLength(0);
      lastTextRef.current = text;
    }

    const tickMs = Math.max(1, Math.round(1000 / speed));
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const startTimer = setTimeout(() => {
      if (cancelled) return;
      intervalId = setInterval(() => {
        setVisibleLength((prev) => {
          if (prev >= text.length) {
            if (intervalId) clearInterval(intervalId);
            onCompleteRef.current?.();
            return prev;
          }
          return prev + 1;
        });
      }, tickMs);
    }, startDelay);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [text, speed, startDelay, reducedMotion, retypeOnTextChange]);

  const slice = useMemo(() => text.slice(0, visibleLength), [text, visibleLength]);
  const isTyping = visibleLength < text.length;

  return (
    <span className={className} style={style}>
      {renderText ? renderText(slice) : slice}
      {showCursor && isTyping && cursor}
    </span>
  );
}

Typewriter.displayName = 'Typewriter';
